import type { FC, ReactNode, RefObject } from "react";
import {
  ChakraModal,
  ChakraModalBody,
  ChakraModalContent,
  ChakraModalFooter,
  ChakraModalHeader,
  ChakraModalOverlay,
  Heading,
} from "@hightouch/htui";

export type TallFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Right-aligned action buttons rendered in the footer. */
  footer: ReactNode;
  /**
   * Max width token for the dialog. Defaults to a single-column width; pass a
   * wider token (e.g. "5xl") for multi-column forms.
   */
  maxWidth?: string;
  /**
   * By default the modal body scrolls as one unit. Set this when the content
   * owns its own scrolling instead (e.g. a fixed search bar above a scrolling
   * grid): the modal is pinned at its full 80vh and the body becomes a
   * non-scrolling flex column, so children can split into fixed and
   * `overflowY="auto"` regions.
   */
  innerScroll?: boolean;
  /** Where focus goes when the modal closes. Defaults to the element that opened it. */
  finalFocusRef?: RefObject<HTMLElement>;
};

/**
 * A tall, scrollable form modal for content-heavy flows (asset pickers, long
 * generation forms). The built-in `Dialog` caps its height and clips these
 * forms, so we drive a `ChakraModal` directly: fixed 80vh max height with the
 * body scrolling inside a pinned header/footer. Mirrors the shell
 * `campaign-context-modal` already uses for the intake form.
 */
export const TallFormModal: FC<TallFormModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = "2xl",
  innerScroll = false,
  finalFocusRef,
}) => (
  <ChakraModal
    finalFocusRef={finalFocusRef}
    closeOnEsc
    closeOnOverlayClick
    isCentered
    scrollBehavior="inside"
    isOpen={isOpen}
    onClose={onClose}
  >
    <ChakraModalOverlay />
    <ChakraModalContent
      maxWidth={maxWidth}
      maxHeight="80vh"
      // A stable height keeps the caller's inner scroll region from resizing
      // as content loads.
      height={innerScroll ? "80vh" : undefined}
      padding={0}
      my="auto"
      sx={{ transform: "none !important" }}
    >
      <ChakraModalHeader
        px={6}
        py={4}
        m={0}
        borderBottomWidth="hairline"
        borderBottomStyle="solid"
        borderColor="base.border"
      >
        <Heading>{title}</Heading>
      </ChakraModalHeader>
      <ChakraModalBody
        py={6}
        {...(innerScroll
          ? {
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }
          : {})}
      >
        {children}
      </ChakraModalBody>
      <ChakraModalFooter
        borderTop="solid 1px"
        borderTopColor="base.border"
        bg="white"
        m={0}
        px={6}
        py={4}
        gap={3}
        borderBottomRadius="xl"
        justifyContent="flex-end"
      >
        {footer}
      </ChakraModalFooter>
    </ChakraModalContent>
  </ChakraModal>
);
